import { useCallback, useEffect, useRef, useState } from "react";
import { fetchRun, fetchRuns } from "../api/client";
import type { RunSummary } from "../types/api";
import {
  formatDurationMs,
  humanizeSpecialty,
  relativeTime,
  shortRunId,
} from "../utils/format";

interface RunHistoryModalProps {
  open: boolean;
  onClose: () => void;
  onSelectRun: (runId: string) => void;
  onStartSearch: () => void;
}

export function RunHistoryModal({
  open,
  onClose,
  onSelectRun,
  onStartSearch,
}: RunHistoryModalProps) {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRuns();
      setRuns(data.runs ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load run history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        ref={panelRef}
        className="modal-panel history-panel"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Run history"
      >
        <div className="modal-hdr">
          <h2>Run history</h2>
          <div className="modal-hdr-actions">
            <button type="button" className="btn-secondary btn-sm" onClick={() => void load()}>
              Refresh
            </button>
            <button type="button" className="modal-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="history-body">
          {loading ? (
            <div className="history-skeleton">
              {[1, 2, 3].map((i) => (
                <div key={i} className="history-row sk-row" />
              ))}
            </div>
          ) : error ? (
            <div className="history-error">
              <p>{error}</p>
              <button type="button" className="btn-secondary" onClick={() => void load()}>
                Retry
              </button>
            </div>
          ) : runs.length === 0 ? (
            <div className="history-empty">
              <p>No past runs yet.</p>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  onClose();
                  onStartSearch();
                }}
              >
                Start a search
              </button>
            </div>
          ) : (
            <ul className="history-list">
              {runs.map((run) => (
                <li key={run.run_id}>
                  <button
                    type="button"
                    className="history-row"
                    onClick={() => {
                      onClose();
                      onSelectRun(run.run_id);
                    }}
                  >
                    <div className="history-row-top">
                      <span className="history-query">{run.query}</span>
                      {run.department_label ? (
                        <span className="framework-badge">{run.department_label}</span>
                      ) : null}
                      {run.framework ? (
                        <span className="framework-badge">{run.framework.toUpperCase()}</span>
                      ) : null}
                    </div>
                    <p className="history-meta">
                      {(run.specialists_used ?? []).map(humanizeSpecialty).join(", ") || "—"}
                      {" · "}
                      {run.paper_count ?? 0} papers
                      {" · "}
                      {formatDurationMs(run.duration_ms)}
                      {" · "}
                      {relativeTime(run.completed_at ?? run.started_at)}
                      {" · "}
                      run {shortRunId(run.run_id)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export async function loadSavedRun(runId: string) {
  return fetchRun(runId);
}
